import { useEffect } from "react";
import { Skeleton, Stack } from "@mui/material";
import { Comment, SendComment } from "./CommentsComponents";
import {
  CommentOut,
  useCommentsApiV1GetCommentForOrderQuery,
  useCommentsApiV1PostCommentMutation,
  useCommentsApiV1SetCommentsReadMutation,
  useNotificationsApiV1UpdateNotificationsMutation,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";

interface Props {
  orderGroupId: number;
  orderId: number;
}

const Comments = (props: Props) => {
  const { orderId, orderGroupId } = props;

  const { data: commentData } = useCommentsApiV1GetCommentForOrderQuery({
    orderGroupId,
  });

  const { data: patientData } = useWebApiV1OrderQuery({ orderId });
  const [postComment] = useCommentsApiV1PostCommentMutation();
  const [setCommentsRead] = useCommentsApiV1SetCommentsReadMutation();
  const [updateNotifications] =
    useNotificationsApiV1UpdateNotificationsMutation();

  // Set comments to read and update the comments notifications
  useEffect(() => {
    if (orderGroupId) {
      setCommentsRead({ orderGroupId });
      updateNotifications({
        orderGroupId,
        notificationIn: { notificationType: "NEW_COMMENT" },
      });
    }
  }, []);

  const sendComment = (content: string, fileIDs: number[]) => {
    if (!orderGroupId) return;

    postComment({
      orderGroupId,
      commentIn: {
        content,
        orderId: Number(orderId),
        attachmentIds: fileIDs,
      },
    });
  };

  const isLoading = !commentData || !patientData;

  if (!orderGroupId) {
    return null;
  }

  return (
    <Stack
      data-testid="comment-list"
      sx={{
        gap: 1,
      }}
    >
      <SendComment
        orderGroupId={orderGroupId}
        sendComment={sendComment}
        isLoading={isLoading}
      />
      <Stack
        direction="column-reverse"
        sx={{
          gap: 1,
        }}
      >
        {isLoading ? (
          <>
            <Skeleton variant="rounded" height={75} />
            <Skeleton variant="rounded" height={75} />
            <Skeleton variant="rounded" height={75} />
            <Skeleton variant="rounded" height={75} />
          </>
        ) : (
          commentData.map((comment: CommentOut) => (
            <Comment key={`comment-${comment?.id}`} {...comment} />
          ))
        )}
      </Stack>
    </Stack>
  );
};

export default Comments;
